import { deepEqual } from '@/utils/function'
import React, { useEffect, useRef, useState } from 'react'
import { AssetFormData } from './use-create-asset-form'

/**
 * Save draft cho form Tạo tài liệu (useState-based, không phải react-hook-form).
 * Persist dữ liệu form có thể serialize vào localStorage; KHÔNG lưu file
 * (File/uploadedFiles không serialize được). Khi quay lại (F5) sẽ hỏi khôi phục.
 */

// Cố ý chỉ loại `files` và `uploadedFiles` vì File không serialize được.
type Step1Draft = Pick<
    AssetFormData,
    | 'assetName'
    | 'assetDescription'
    | 'documentGroup'
    | 'documentType'
    | 'confidentialityLevel'
    | 'integrityLevel'
    | 'availabilityLevel'
    | 'departmentId'
    | 'shareScope'
    | 'shared_employee_ids'
    | 'shared_department_ids'
    | 'startDate'
    | 'endDate'
    | 'notes'
    | 'isReadConfirmationRequired'
    | 'notifyScope'
    | 'notify_employee_ids'
    | 'notify_department_ids'
>

const pickStep1 = (data: AssetFormData): Step1Draft => ({
    assetName: data.assetName,
    assetDescription: data.assetDescription,
    documentGroup: data.documentGroup,
    documentType: data.documentType,
    confidentialityLevel: data.confidentialityLevel,
    integrityLevel: data.integrityLevel,
    availabilityLevel: data.availabilityLevel,
    departmentId: data.departmentId,
    shareScope: data.shareScope,
    shared_employee_ids: data.shared_employee_ids,
    shared_department_ids: data.shared_department_ids,
    startDate: data.startDate,
    endDate: data.endDate,
    notes: data.notes,
    isReadConfirmationRequired: data.isReadConfirmationRequired,
    notifyScope: data.notifyScope,
    notify_employee_ids: data.notify_employee_ids,
    notify_department_ids: data.notify_department_ids,
})

interface UseAssetDraftPersistenceParams {
    formData: AssetFormData
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
    defaultValues: AssetFormData
    storageKey: string
    /** Chỉ bật ở chế độ tạo mới (không bật cho update/edit). */
    enabled: boolean
}

export const useAssetDraftPersistence = ({
    formData,
    setFormData,
    defaultValues,
    storageKey,
    enabled,
}: UseAssetDraftPersistenceParams) => {
    const [shouldRestore, setShouldRestore] = useState(false)
    const draftRef = useRef<Step1Draft | null>(null)
    const initializedRef = useRef(false)

    // Khi mount: kiểm tra draft đã lưu (1 lần, chỉ create mode)
    useEffect(() => {
        if (!enabled || initializedRef.current) return
        initializedRef.current = true

        const saved = localStorage.getItem(storageKey)
        if (!saved) return

        try {
            const parsed = JSON.parse(saved) as Step1Draft
            if (deepEqual(parsed, pickStep1(defaultValues))) {
                localStorage.removeItem(storageKey)
                return
            }
            draftRef.current = parsed
            setShouldRestore(true)
        } catch {
            localStorage.removeItem(storageKey)
        }
    }, [enabled, storageKey, defaultValues])

    // Auto-save Step 1 mỗi khi formData đổi (không lưu khi đang chờ user quyết định khôi phục)
    useEffect(() => {
        if (!enabled || shouldRestore) return
        try {
            localStorage.setItem(
                storageKey,
                JSON.stringify(pickStep1(formData))
            )
        } catch (e) {
            console.warn('Không thể lưu draft tài liệu', e)
        }
    }, [enabled, shouldRestore, formData, storageKey])

    const acceptRestore = () => {
        if (draftRef.current) {
            setFormData((prev) => ({ ...prev, ...draftRef.current }))
        }
        draftRef.current = null
        setShouldRestore(false)
    }

    const declineRestore = () => {
        localStorage.removeItem(storageKey)
        draftRef.current = null
        setShouldRestore(false)
    }

    const clearDraft = () => {
        try {
            localStorage.removeItem(storageKey)
        } catch (e) {
            console.warn('Không thể xóa draft tài liệu', e)
        }
    }

    return { shouldRestore, acceptRestore, declineRestore, clearDraft }
}
